import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';
import { Tag } from '../models/tags.model';
import { Store } from '@ngrx/store';
import { State } from '../reducers';
import { map, tap, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class TagService {
  private readonly CACHE_KEY = 'cached_tags';
  private tagsSubject = new BehaviorSubject<Tag[]>([]);

  tags$ = this.store.select((store) => store.tags).pipe(
    tap(tags => {
      if (tags?.length) {
        this.storeTags(tags);
      }
    })
  );

  tagMapping$ = this.tags$.pipe(
    map((tags) => {
      let toTag = new Map<number, Tag>();
      tags.forEach((tag) => {
        toTag.set(tag.uid, tag);
      });
      return toTag;
    })
  );

  constructor(
    private http: HttpClient, 
    private store: Store<State>
  ) {
    this.initializeTags();
  }

  private initializeTags(): void {
    // Try to load cached tags first
    const cachedTags = this.loadCachedTags();
    if (cachedTags?.length) {
      this.tagsSubject.next(cachedTags);
    }
    
    // Then request fresh tags
    this.requestFactors().pipe(
      tap(tags => this.storeTags(tags)),
      catchError(error => {
        console.error('Error loading tags:', error);
        return [];
      })
    ).subscribe();
  }

  private storeTags(tags: Tag[]): void {
    try {
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(tags));
      this.tagsSubject.next(tags);
    } catch (e) {
      console.error('Error caching tags:', e);
    }
  }

  private loadCachedTags(): Tag[] {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      return cached ? JSON.parse(cached) : [];
    } catch (e) {
      console.error('Error loading cached tags:', e);
      return [];
    }
  }

  public saveFactors(factors: Tag[]): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    return this.http.post(
      `${environment.SERVER_URL}/tags`, 
      factors, 
      httpOptions
    ).pipe(
      tap(response => {
        this.storeTags(factors);
        return response;
      })
    );
  }

  requestFactors(): Observable<Tag[]> {
    return this.http.get<Tag[]>(`${environment.SERVER_URL}/tags`).pipe(
      tap(tags => {
        if (tags?.length) {
          this.storeTags(tags);
        }
      }),
      catchError(error => {
        console.error('Error fetching tags:', error);
        // Return cached tags if available
        const cached = this.loadCachedTags();
        return cached?.length ? [cached] : [];
      })
    );
  }
}